import { useState, useEffect, useMemo } from 'react'
import {
  fetchLatestSnapshot,
  fetchSnapshots,
  fetchPositions,
  fetchSignalStates,
  fetchJournalEntries,
  fetchActiveSymbols,
  fetchPriceData,
  fetchRegimeState,
  fetchStrategyConfig,
  upsertJournalEntry,
  DEMO_JOURNAL,
  type PortfolioSnapshot,
  type Position,
  type SignalState,
  type Symbol,
  type JournalEntry,
  type JournalEntryInsert,
  type PriceBar,
  type RegimeState,
  type StrategyConfig,
} from './lib/supabase'
import { PortfolioOverview } from './components/PortfolioOverview'
import { StrategyPlaybook } from './components/StrategyPlaybook'
import { PositionsTable } from './components/PositionsTable'
import { ExposureChart } from './components/ExposureChart'
import { PerformanceChart } from './components/PerformanceChart'
import { SignalStatus } from './components/SignalStatus'
import { TradeJournal } from './components/TradeJournal'
import { LiveAnalysis } from './components/LiveAnalysis'
import { StrategyView } from './components/strategy/StrategyView'
import { format } from 'date-fns'

// Demo data — reflects actual TradeStation account scaled 10x (actual: ~$33.5k → displayed: ~$335k)
// Sleeve breakdown: ETF 9.3% (XBI/XLE/XLI/XLP/XLRE/XLU/XLV), Stock 24.5%, IPO 0% (no current IPO positions)
const DEMO_SNAPSHOT: PortfolioSnapshot = {
  id: 1,
  snapshot_date: new Date().toISOString().split('T')[0],
  account_id: 'demo',
  net_worth: 335407,
  cash: 222087,
  positions_value: 113319,
  total_exposure_pct: 33.8,
  stock_exposure_pct: 24.5,
  etf_exposure_pct: 9.3,
  ipo_exposure_pct: 0,
  commod_exposure_pct: 0,
  crypto_exposure_pct: 0,
  position_count: 17,
  daily_pnl: -3123,
  daily_pnl_pct: -0.931,
  drawdown_pct: 1.08,
  high_water_mark: 339022,
  stock_position_count: null,
  etf_position_count: null,
  ipo_position_count: null,
  unrealized_pnl: null,
  open_risk_pct: null,
  is_live: null,
  trading_days_gap: null,
  heartbeat_at: null,
  trailing_perf: null,
}

const DEMO_POSITIONS: Position[] = [
  // ETF Sleeve
  { id: 1,  account_id: 'demo', ticker: 'XBI',  quantity: 40,  avg_cost: 126.37, current_price: 123.17, unrealized_pnl: -1280,   market_value: 4926.80,  position_pct: 1.5, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 118.00, vwap_stop: 120.00, extreme_stop: 115.00, synced_at: new Date().toISOString() },
  { id: 2,  account_id: 'demo', ticker: 'XLE',  quantity: 80,  avg_cost: 56.67,  current_price: 56.26,  unrealized_pnl: -328,    market_value: 4500.40,  position_pct: 1.3, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 53.00, vwap_stop: 54.00,  extreme_stop: 51.00,  synced_at: new Date().toISOString() },
  { id: 3,  account_id: 'demo', ticker: 'XLI',  quantity: 20,  avg_cost: 178.07, current_price: 170.42, unrealized_pnl: -1530,   market_value: 3408.40,  position_pct: 1.0, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 163.00, vwap_stop: 166.00, extreme_stop: 159.00, synced_at: new Date().toISOString() },
  { id: 4,  account_id: 'demo', ticker: 'XLP',  quantity: 50,  avg_cost: 89.09,  current_price: 85.80,  unrealized_pnl: -1645,   market_value: 4290.00,  position_pct: 1.3, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 82.00, vwap_stop: 84.00,  extreme_stop: 80.00,  synced_at: new Date().toISOString() },
  { id: 5,  account_id: 'demo', ticker: 'XLRE', quantity: 110, avg_cost: 43.78,  current_price: 43.09,  unrealized_pnl: -759,    market_value: 4739.90,  position_pct: 1.4, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 40.50, vwap_stop: 41.50, extreme_stop: 39.00, synced_at: new Date().toISOString() },
  { id: 6,  account_id: 'demo', ticker: 'XLU',  quantity: 100, avg_cost: 47.61,  current_price: 46.76,  unrealized_pnl: -855,    market_value: 4675.50,  position_pct: 1.4, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 44.00, vwap_stop: 45.00,  extreme_stop: 42.50, synced_at: new Date().toISOString() },
  { id: 7,  account_id: 'demo', ticker: 'XLV',  quantity: 30,  avg_cost: 158.77, current_price: 153.11, unrealized_pnl: -1700,   market_value: 4593.15,  position_pct: 1.4, sleeve: 'etf',   entry_date: '2026-03-02', entry_signal: 'Trnd-LEntry', stop_price: 147.00, vwap_stop: 150.00, extreme_stop: 143.00, synced_at: new Date().toISOString() },
  // Stock Sleeve
  { id: 8,  account_id: 'demo', ticker: 'AMGN', quantity: 90,  avg_cost: 375.18, current_price: 365.84, unrealized_pnl: -8409,   market_value: 32925.15, position_pct: 9.8, sleeve: 'stock', entry_date: '2026-03-05', entry_signal: 'Trnd-LEntry', stop_price: 352.00, vwap_stop: 358.00, extreme_stop: 344.00, synced_at: new Date().toISOString() },
  { id: 9,  account_id: 'demo', ticker: 'BIIB', quantity: 10,  avg_cost: 187.97, current_price: 187.25, unrealized_pnl: -72,     market_value: 1872.50,  position_pct: 0.6, sleeve: 'stock', entry_date: '2026-03-05', entry_signal: 'Trnd-LEntry', stop_price: 176.00, vwap_stop: 180.00, extreme_stop: 172.00, synced_at: new Date().toISOString() },
  { id: 10, account_id: 'demo', ticker: 'CF',   quantity: 20,  avg_cost: 104.83, current_price: 109.48, unrealized_pnl: 929,     market_value: 2189.50,  position_pct: 0.7, sleeve: 'stock', entry_date: '2026-03-04', entry_signal: 'NewHi',       stop_price: 98.00,  vwap_stop: 100.50, extreme_stop: 95.00,  synced_at: new Date().toISOString() },
  { id: 11, account_id: 'demo', ticker: 'DE',   quantity: 40,  avg_cost: 617.59, current_price: 588.33, unrealized_pnl: -11702,  market_value: 23533.20, position_pct: 7.0, sleeve: 'stock', entry_date: '2026-03-04', entry_signal: 'Trnd-LEntry', stop_price: 575.00, vwap_stop: 585.00, extreme_stop: 562.00, synced_at: new Date().toISOString() },
  { id: 12, account_id: 'demo', ticker: 'DGX',  quantity: 10,  avg_cost: 206.21, current_price: 205.27, unrealized_pnl: -94,     market_value: 2052.70,  position_pct: 0.6, sleeve: 'stock', entry_date: '2026-03-05', entry_signal: 'Trnd-LEntry', stop_price: 193.00, vwap_stop: 197.00, extreme_stop: 189.00, synced_at: new Date().toISOString() },
  { id: 13, account_id: 'demo', ticker: 'EIX',  quantity: 30,  avg_cost: 74.34,  current_price: 70.85,  unrealized_pnl: -1047,   market_value: 2125.50,  position_pct: 0.6, sleeve: 'stock', entry_date: '2026-03-04', entry_signal: 'Trnd-LEntry', stop_price: 67.00, vwap_stop: 68.50,  extreme_stop: 65.00,  synced_at: new Date().toISOString() },
  { id: 14, account_id: 'demo', ticker: 'KIM',  quantity: 100, avg_cost: 23.37,  current_price: 23.44,  unrealized_pnl: 65,      market_value: 2343.50,  position_pct: 0.7, sleeve: 'stock', entry_date: '2026-03-05', entry_signal: 'Trnd-LEntry', stop_price: 21.50, vwap_stop: 22.00,  extreme_stop: 20.50,  synced_at: new Date().toISOString() },
  { id: 15, account_id: 'demo', ticker: 'ODFL', quantity: 10,  avg_cost: 216.85, current_price: 208.21, unrealized_pnl: -864,    market_value: 2082.10,  position_pct: 0.6, sleeve: 'stock', entry_date: '2026-03-04', entry_signal: 'Trnd-LEntry', stop_price: 198.00, vwap_stop: 203.00, extreme_stop: 194.00, synced_at: new Date().toISOString() },
  { id: 16, account_id: 'demo', ticker: 'PWR',  quantity: 20,  avg_cost: 571.66, current_price: 537.11, unrealized_pnl: -6910,   market_value: 10742.20, position_pct: 3.2, sleeve: 'stock', entry_date: '2026-03-04', entry_signal: 'Trnd-LEntry', stop_price: 524.00, vwap_stop: 534.00, extreme_stop: 512.00, synced_at: new Date().toISOString() },
  { id: 17, account_id: 'demo', ticker: 'T',    quantity: 80,  avg_cost: 28.92,  current_price: 28.98,  unrealized_pnl: 48,      market_value: 2318.40,  position_pct: 0.7, sleeve: 'stock',  entry_date: '2026-03-05', entry_signal: 'Trnd-LEntry', stop_price: 26.50,  vwap_stop: 27.50,  extreme_stop: 25.50,  synced_at: new Date().toISOString() },
  // Commodity Sleeve
  { id: 18, account_id: 'demo', ticker: 'GLD',  quantity: 30,  avg_cost: 218.45, current_price: 224.30, unrealized_pnl: 1755,    market_value: 6729.00,  position_pct: 2.0, sleeve: 'commod', entry_date: '2026-03-10', entry_signal: 'Trnd-LEntry', stop_price: 210.00, vwap_stop: 214.00, extreme_stop: 205.00, synced_at: new Date().toISOString() },
  // Crypto Sleeve
  { id: 19, account_id: 'demo', ticker: 'IBIT', quantity: 80,  avg_cost: 52.18,  current_price: 55.40,  unrealized_pnl: 2576,    market_value: 4432.00,  position_pct: 1.3, sleeve: 'crypto', entry_date: '2026-03-12', entry_signal: 'Trnd-LEntry', stop_price: 49.00,  vwap_stop: 51.00,  extreme_stop: 47.00,  synced_at: new Date().toISOString() },
]

const DEMO_SIGNALS: SignalState[] = [
  { id: 1,  ticker: 'AMGN', date: new Date().toISOString().split('T')[0], rs_check: true,  rs_diff: 1.8,  vwap_from_low: 358.00, vwap_qual: true,  pct_chg: 4.2,  pct_chg_check: true,  mom_check: true,  price_check: true,  vol_check: true,  rsp_over_vwap: true,  trend_qual: true,  entry_signal: null },
  { id: 2,  ticker: 'DE',   date: new Date().toISOString().split('T')[0], rs_check: true,  rs_diff: 2.1,  vwap_from_low: 585.00, vwap_qual: true,  pct_chg: 6.8,  pct_chg_check: true,  mom_check: true,  price_check: true,  vol_check: true,  rsp_over_vwap: true,  trend_qual: true,  entry_signal: null },
  { id: 3,  ticker: 'PWR',  date: new Date().toISOString().split('T')[0], rs_check: true,  rs_diff: 3.4,  vwap_from_low: 534.00, vwap_qual: true,  pct_chg: 9.1,  pct_chg_check: true,  mom_check: true,  price_check: true,  vol_check: true,  rsp_over_vwap: true,  trend_qual: true,  entry_signal: null },
  { id: 4,  ticker: 'CF',   date: new Date().toISOString().split('T')[0], rs_check: true,  rs_diff: 4.2,  vwap_from_low: 100.50, vwap_qual: true,  pct_chg: 11.3, pct_chg_check: true,  mom_check: true,  price_check: true,  vol_check: true,  rsp_over_vwap: true,  trend_qual: true,  entry_signal: 'NewHi' },
  { id: 5,  ticker: 'XBI',  date: new Date().toISOString().split('T')[0], rs_check: false, rs_diff: -2.8, vwap_from_low: 120.00, vwap_qual: true,  pct_chg: 3.1,  pct_chg_check: true,  mom_check: false, price_check: true,  vol_check: true,  rsp_over_vwap: false, trend_qual: false, entry_signal: null },
  { id: 6,  ticker: 'XLV',  date: new Date().toISOString().split('T')[0], rs_check: false, rs_diff: -1.9, vwap_from_low: 150.00, vwap_qual: true,  pct_chg: 2.4,  pct_chg_check: true,  mom_check: false, price_check: true,  vol_check: true,  rsp_over_vwap: true,  trend_qual: false, entry_signal: null },
]

// Demo regime state — neutral 1.00× across all sleeves (no live regime data)
const DEMO_REGIME_STATE: RegimeState = {
  id: 1,
  computed_date: new Date().toISOString().split('T')[0],
  computed_at: new Date().toISOString(),
  breadth_score: 50,
  spy_above_200sma: true,
  regime_scalar: 1.00,
  tier: 3,
  universe_count: 141,
  entry_count: 70,
  strong_count: 28,
  ipo_scalar: 1.00,
  ipo_rs_vs_spy: 0,
  commod_scalar: 1.00,
  commod_breadth: 50,
  crypto_scalar: 1.00,
  crypto_breadth: 50,
  // range_gate_* values are diagnostic only (D079: the range gate is removed from all sleeves)
  dynamic_tq_params: {
    etf:    { range_gate_ref: 0.948, range_gate_k: 0.52, range_ratio: 1.00, regime_bucket: 'normal',   efficiency_ratio: 0.52, benchmark: 'RSP'  },
    stock:  { range_gate_ref: 0.948, range_gate_k: 0.85, range_ratio: 1.00, regime_bucket: 'normal',   efficiency_ratio: 0.52, benchmark: 'RSP'  },
    ipo:    { range_gate_ref: 1.851, range_gate_k: 0.70, range_ratio: 1.00, regime_bucket: 'normal',   efficiency_ratio: 0.48, benchmark: 'IPO'  },
    commod: { range_gate_ref: 1.822, range_gate_k: 0.70, range_ratio: 1.15, regime_bucket: 'expanded', efficiency_ratio: 0.61, benchmark: 'GSG'  },
    crypto: { range_gate_ref: 2.219, range_gate_k: 0.70, range_ratio: 0.88, regime_bucket: 'compressed', efficiency_ratio: 0.44, benchmark: 'IBIT' },
  },
  notes: 'Demo neutral regime',
}

function generateDemoRSPPrices(snapshots: PortfolioSnapshot[]): PriceBar[] {
  const rng = seededRng(20260305 + 7)
  let price = 165.00
  return snapshots.map(s => {
    const change = (rng() - 0.47) * 0.011 // slight positive drift, lower vol than portfolio
    price = price * (1 + change)
    return {
      date: s.snapshot_date,
      open: price,
      high: price * (1 + rng() * 0.002),
      low: price * (1 - rng() * 0.002),
      close: Math.round(price * 100) / 100,
      volume: 0,
    }
  })
}

// Portfolio period selector config
type PeriodKey = 'today' | 'trailing5' | 'trailing10' | 'trailing20' | 'trailing60'
const PERIOD_CONFIGS: Record<PeriodKey, { label: string; days: number; chartDays: number; chartTitle: string }> = {
  today:      { label: 'Today',  days: 1,  chartDays: 2,  chartTitle: 'Today' },
  trailing5:  { label: 'T-5',   days: 5,  chartDays: 5,  chartTitle: 'Trailing 5 Days' },
  trailing10: { label: 'T-10',  days: 10, chartDays: 10, chartTitle: 'Trailing 10 Days' },
  trailing20: { label: 'T-20',  days: 20, chartDays: 20, chartTitle: 'Trailing 20 Days' },
  trailing60: { label: 'T-60',  days: 60, chartDays: 60, chartTitle: 'Trailing 60 Days' },
}
const PERIOD_KEYS = Object.keys(PERIOD_CONFIGS) as PeriodKey[]

// Seeded PRNG — same chart every load (no Math.random drift)
function seededRng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = Math.imul(1664525, s) + 1013904223 >>> 0
    return s / 4294967296
  }
}

function generateDemoSnapshots(): PortfolioSnapshot[] {
  const snapshots: PortfolioSnapshot[] = []
  const rng = seededRng(20260305)
  // Anchor: HWM ~$342k, drifts down to current $335,407 over 30 days
  let netWorth = 342000
  let highWaterMark = netWorth

  for (let i = 60; i >= 0; i--) {
    const date = new Date()
    date.setDate(date.getDate() - i)

    // Slight downward bias (-0.064%/day avg) with ±1% daily noise to land near $335,407
    const dailyChange = (rng() - 0.534) * 0.018
    netWorth = netWorth * (1 + dailyChange)
    highWaterMark = Math.max(highWaterMark, netWorth)

    const drawdown = ((highWaterMark - netWorth) / highWaterMark) * 100
    const totalExposure = 28 + rng() * 14

    snapshots.push({
      id: i,
      snapshot_date: date.toISOString().split('T')[0],
      account_id: 'live',
      net_worth: netWorth,
      cash: netWorth * (1 - totalExposure / 100),
      positions_value: netWorth * (totalExposure / 100),
      total_exposure_pct: totalExposure,
      stock_exposure_pct: totalExposure * 0.50,
      etf_exposure_pct: totalExposure * 0.27,
      ipo_exposure_pct: 0,
      commod_exposure_pct: totalExposure * 0.15,
      crypto_exposure_pct: totalExposure * 0.08,
      position_count: 17,
      daily_pnl: netWorth * dailyChange,
      daily_pnl_pct: dailyChange * 100,
      drawdown_pct: drawdown,
      high_water_mark: highWaterMark,
      stock_position_count: null,
      etf_position_count: null,
      ipo_position_count: null,
      unrealized_pnl: null,
      open_risk_pct: null,
      is_live: null,
      trading_days_gap: null,
      heartbeat_at: null,
      trailing_perf: null,
    })
  }

  return snapshots
}

export default function App() {
  const [snapshot, setSnapshot] = useState<PortfolioSnapshot | null>(null)
  const [snapshots, setSnapshots] = useState<PortfolioSnapshot[]>([])
  const [benchmarkPrices, setBenchmarkPrices] = useState<PriceBar[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [symbols, setSymbols] = useState<Symbol[]>([])
  const [signals, setSignals] = useState<SignalState[]>([])
  const [regimeState, setRegimeState] = useState<RegimeState | null>(null)
  const [strategyConfig, setStrategyConfig] = useState<StrategyConfig | null>(null)
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [journalPrefillTicker, setJournalPrefillTicker] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const [isDemoMode, setIsDemoMode] = useState(false)
  const [isSignalsStale, setIsSignalsStale] = useState(false)
  const [isSnapshotStale, setIsSnapshotStale] = useState(false)
  const [isRspStale, setIsRspStale] = useState(false)
  const [activeView, setActiveView] = useState<'dashboard' | 'analysis' | 'strategy'>('dashboard')
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('today')

  useEffect(() => {
    async function loadData() {
      setLoading(true)

      try {
        const [snapshotData, snapshotsData, positionsData, symbolsData, signalsData, journalData, rspData, regimeData, strategyConfigData] = await Promise.all([
          fetchLatestSnapshot(),
          fetchSnapshots(60),
          fetchPositions(),
          fetchActiveSymbols(),
          fetchSignalStates(),
          fetchJournalEntries(),
          fetchPriceData('RSP', 65),
          fetchRegimeState(),
          fetchStrategyConfig(),
        ])

        const isLive = !!snapshotData
        setSnapshot(snapshotData || DEMO_SNAPSHOT)
        // Only use demo history/signals/positions in full demo mode — never mix demo with live data
        const liveSnapshots = snapshotsData.length > 0 ? snapshotsData : (isLive ? [] : generateDemoSnapshots())
        setSnapshots(liveSnapshots)
        // RSP benchmark: use live data if available, else generate demo aligned to snapshot dates
        if (rspData.length > 0) {
          setBenchmarkPrices(rspData)
          const latestRsp = rspData[rspData.length - 1].date
          const today = new Date().toLocaleDateString('en-CA')
          // RSP is stale if latest bar is 3+ calendar days old.
          // Both sides parse YYYY-MM-DD as UTC midnight, so the diff is timezone-symmetric.
          // >= 3 catches back-to-back Thu+Fri sync failures (visible as stale on Monday).
          const daysDiff = Math.floor((new Date(today).getTime() - new Date(latestRsp).getTime()) / 86400000)
          setIsRspStale(daysDiff >= 3)
        } else if (liveSnapshots.length > 0) {
          // RSP not yet in jgl_price_data — generate synthetic fallback so the comparison
          // is always visible. isRspStale=true triggers the ⚠ RSP stale header badge.
          setBenchmarkPrices(generateDemoRSPPrices(liveSnapshots))
          setIsRspStale(isLive) // stale only in live mode; demo mode is expected to be synthetic
        } else {
          setBenchmarkPrices([])
          setIsRspStale(false)
        }
        setPositions(positionsData.length > 0 ? positionsData : (isLive ? [] : DEMO_POSITIONS))
        setSymbols(symbolsData)
        setSignals(signalsData.length > 0 ? signalsData : (isLive ? [] : DEMO_SIGNALS))
        // Regime state: live row when present; else demo neutral in demo mode; else null (graceful "no data")
        setRegimeState(regimeData ?? (isLive ? null : DEMO_REGIME_STATE))
        // Strategy config: live from jgl_strategy_config (populated by publish_strategy_config.py)
        setStrategyConfig(strategyConfigData)
        // Always set stale flags — never leave in prior-render state
        if (signalsData.length > 0) {
          const newest = signalsData.reduce((a, b) => a.date > b.date ? a : b)
          setIsSignalsStale(newest.date < new Date().toLocaleDateString('en-CA'))
        } else {
          setIsSignalsStale(false)
        }
        setIsSnapshotStale(
          snapshotData
            ? snapshotData.snapshot_date < new Date().toLocaleDateString('en-CA')
            : false
        )
        const fullDemoMode = !snapshotData && snapshotsData.length === 0 && positionsData.length === 0
        setJournalEntries(fullDemoMode && journalData.length === 0 ? DEMO_JOURNAL : journalData)
        setIsDemoMode(fullDemoMode)
        setLastUpdate(new Date())
      } catch {
        setSnapshot(DEMO_SNAPSHOT)
        const fallbackSnaps = generateDemoSnapshots()
        setSnapshots(fallbackSnaps)
        setBenchmarkPrices(generateDemoRSPPrices(fallbackSnaps))
        setPositions(DEMO_POSITIONS)
        setSymbols([])
        setSignals(DEMO_SIGNALS)
        setRegimeState(DEMO_REGIME_STATE)
        setIsSignalsStale(false)
        setIsSnapshotStale(false)
        setIsRspStale(false)
        setJournalEntries(DEMO_JOURNAL)
        setIsDemoMode(true)
      }

      setLoading(false)
    }

    loadData()

    // Refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const handleJournalSave = async (entry: Partial<JournalEntryInsert> & { id?: number }) => {
    // Optimistic local update
    const newEntry: JournalEntry = {
      id: entry.id || Date.now(),
      account_id: entry.account_id || 'live',
      trade_id: entry.trade_id || null,
      ticker: entry.ticker || null,
      entry_type: entry.entry_type || 'trade_review',
      trade_date: entry.trade_date || new Date().toISOString().split('T')[0],
      thesis: entry.thesis || null,
      post_review: entry.post_review || null,
      emotion_before: entry.emotion_before || null,
      emotion_after: entry.emotion_after || null,
      setup_quality: entry.setup_quality || null,
      rule_adherence: entry.rule_adherence || null,
      execution_quality: entry.execution_quality || null,
      tags: entry.tags || [],
      outcome: entry.outcome || null,
      pnl_at_review: entry.pnl_at_review || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    if (entry.id) {
      setJournalEntries(prev => prev.map(e => e.id === entry.id ? newEntry : e))
    } else {
      setJournalEntries(prev => [newEntry, ...prev])
    }
    // Persist to Supabase if connected
    upsertJournalEntry(entry).then(saved => {
      if (saved) fetchJournalEntries().then(setJournalEntries)
    }).catch((err) => {
      console.error('Journal save failed:', err)
      // Roll back optimistic update
      setJournalEntries(prev => entry.id
        ? prev.map(e => e.id === entry.id ? (e.id === newEntry.id ? e : e) : e)
        : prev.filter(e => e.id !== newEntry.id)
      )
      alert('Journal save failed — entry not persisted. Check connection.')
    })
  }

  // Period-filtered snapshots for chart — date-based cutoff using RSP calendar (not row count)
  const filteredSnapshots = useMemo(() => {
    const days = PERIOD_CONFIGS[activePeriod].chartDays
    if (!benchmarkPrices.length) return snapshots.slice(-days)
    const sorted = [...benchmarkPrices].sort((a, b) => a.date.localeCompare(b.date))
    const cutoff = sorted.length > days ? sorted[sorted.length - 1 - days].date : sorted[0]?.date
    return cutoff ? snapshots.filter(s => s.snapshot_date >= cutoff) : snapshots.slice(-days)
  }, [snapshots, activePeriod, benchmarkPrices])

  // Period performance key mapping to trailing_perf JSONB keys (computed server-side)
  const PERIOD_PERF_KEY: Record<string, string> = {
    today: '', trailing5: 't5', trailing10: 't10', trailing20: 't20', trailing60: 't60',
  }

  // Read pre-computed trailing performance from snapshot.trailing_perf (written by portfolio_snapshot.py)
  const precomputedPerf = useMemo(() => {
    const key = PERIOD_PERF_KEY[activePeriod]
    if (!key || !snapshot?.trailing_perf) return null
    return snapshot.trailing_perf[key as keyof typeof snapshot.trailing_perf] ?? null
  }, [snapshot, activePeriod])

  // Derived values for PortfolioOverview — pre-computed by server, no client-side math
  const periodMetrics = useMemo(() => {
    if (activePeriod === 'today') return null
    if (!precomputedPerf?.portfolio_roc) return null
    const endNW = snapshot?.net_worth ?? 0
    const pnl = precomputedPerf.pnl_dollars ?? (endNW * precomputedPerf.portfolio_roc / 100)
    return { pnl, pnlPct: precomputedPerf.portfolio_roc }
  }, [precomputedPerf, activePeriod, snapshot])

  const periodBenchmarkReturn = useMemo(() => {
    if (activePeriod === 'today') return null
    return precomputedPerf?.rsp_roc ?? null
  }, [precomputedPerf, activePeriod])

  // Build sleeve map from full symbol universe; fall back to positions for demo mode
  const sleeveMap = useMemo<Record<string, string>>(() =>
    symbols.length > 0
      ? symbols.reduce((acc, s) => { if (s.sleeve) acc[s.ticker] = s.sleeve; return acc }, {} as Record<string, string>)
      : positions.reduce((acc, p) => { acc[p.ticker] = p.sleeve; return acc }, {} as Record<string, string>),
    [symbols, positions]
  )

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard">
      <header className="header">
        <div className="logo">
          <div className="logo-mark">JGL</div>
          <div>
            <div className="logo-text">JGL Capital</div>
            <div className="logo-subtitle">Trading Operations</div>
          </div>
          {isDemoMode && (
            <div style={{ background: '#dc2626', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '4px 12px', borderRadius: 4, letterSpacing: '0.05em' }}>
              ⚠ LIVE DATA UNAVAILABLE
            </div>
          )}
        </div>
        <div className="header-meta">
          <nav className="header-nav">
            <button
              className={`header-nav-link${activeView === 'dashboard' ? ' active' : ''}`}
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`header-nav-link${activeView === 'analysis' ? ' active' : ''}`}
              onClick={() => setActiveView('analysis')}
            >
              Live Analysis
            </button>
            <button
              className={`header-nav-link${activeView === 'strategy' ? ' active' : ''}`}
              onClick={() => setActiveView('strategy')}
            >
              Strategy
            </button>
            <div className="header-nav-sep" />
            <a
              href="https://inspector.l7-partners.com"
              target="_blank"
              rel="noopener noreferrer"
              className="header-nav-link"
            >
              Strategy Inspector
            </a>
            <a
              href={`${import.meta.env.VITE_JUPYTER_BASE_URL || 'http://localhost:8888'}/lab/tree/playbook.ipynb`}
              target="_blank"
              rel="noopener noreferrer"
              className="header-nav-link"
            >
              Playbook
            </a>
          </nav>
          <div className="last-updated">
            <div className={`status-dot${isSignalsStale || isSnapshotStale || isRspStale ? ' stale' : ''}`} />
            <span>Last sync: {format(lastUpdate, 'HH:mm:ss')}</span>
            {isSnapshotStale && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: 6 }}>⚠ Snapshot stale</span>}
            {isSignalsStale && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: 6 }}>⚠ Signals stale</span>}
            {isRspStale && <span style={{ color: '#f59e0b', fontSize: '0.7rem', marginLeft: 6 }}>⚠ RSP stale</span>}
          </div>
        </div>
      </header>

      <main className="main-content">
        {activeView === 'dashboard' ? (
          <>
            {/* Portfolio Overview */}
            <PortfolioOverview
              snapshot={snapshot}
              periodLabel={PERIOD_CONFIGS[activePeriod].chartTitle}
              periodPnl={periodMetrics?.pnl}
              periodPnlPct={periodMetrics?.pnlPct}
              benchmarkReturnPct={periodBenchmarkReturn ?? undefined}
            />

            {/* Strategy Playbook — sequential decision chain  */}
            <StrategyPlaybook
              snapshot={snapshot}
              regimeState={regimeState}
              signals={signals}
              positions={positions}
              sleeveMap={sleeveMap}
              isSignalsStale={isSignalsStale}
              strategyConfig={strategyConfig}
              onOpenStrategy={() => setActiveView('strategy')}
            />

            {/* Period Selector */}
            <div className="period-selector-bar">
              <span className="period-selector-label">Portfolio View</span>
              <div className="period-toggle">
                {PERIOD_KEYS.map(key => (
                  <button
                    key={key}
                    className={`period-toggle-btn${activePeriod === key ? ' active' : ''}`}
                    onClick={() => setActivePeriod(key)}
                  >
                    {PERIOD_CONFIGS[key].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid-2">
              <PerformanceChart
                snapshots={filteredSnapshots}
                benchmarkPrices={benchmarkPrices}
                periodLabel={PERIOD_CONFIGS[activePeriod].chartTitle}
              />
              <ExposureChart snapshot={snapshot} />
            </div>

            {/* Positions Table */}
            <PositionsTable positions={positions} onJournalClick={(ticker) => setJournalPrefillTicker(ticker)} />

            {/* Trade Journal */}
            <TradeJournal
              entries={journalEntries}
              onSave={handleJournalSave}
              prefillTicker={journalPrefillTicker}
              onClearPrefill={() => setJournalPrefillTicker(null)}
              sleeveMap={sleeveMap}
            />

            {/* Signal Status */}
            <SignalStatus
              signals={signals}
              sleeveMap={sleeveMap}
              positions={positions}
            />
          </>
        ) : activeView === 'strategy' ? (
          <StrategyView
            snapshot={snapshot}
            positions={positions}
            regimeState={regimeState}
            strategyConfig={strategyConfig}
            isDemoMode={isDemoMode}
          />
        ) : (
          <LiveAnalysis
            positions={positions}
            signals={signals}
            snapshot={snapshot}
          />
        )}
      </main>
    </div>
  )
}
